/**
 * PickleballRatingsPage.js — Elo-based player rating leaderboard for pickleball leagues.
 *
 * Features:
 * - Leaderboard sorted by Elo rating (1.0–6.0 DUPR-inspired scale)
 * - Player detail panel with full rating history
 * - Manually add players with a custom starting rating
 * - Manual rating adjustment & partner name entry
 * - Delete a player from ratings
 * - Recalculate all ratings from scratch (replays game results)
 *
 * CSS prefix: pbr-
 */

import React, { useEffect, useState, useCallback } from 'react';
import './PickleballRatingsPage.css';
import {
  getPickleballRatings,
  getPickleballPlayerRating,
  addPickleballPlayer,
  updatePickleballRating,
  deletePickleballRating,
  recalculatePickleballRatings
} from '../services/api';

const SKILL_LABELS = [
  { label: 'Beginner',        min: 1.0, max: 2.5, cls: 'pbr-badge-beginner' },
  { label: 'Novice',          min: 2.5, max: 3.0, cls: 'pbr-badge-novice' },
  { label: 'Intermediate',    min: 3.0, max: 3.5, cls: 'pbr-badge-intermediate' },
  { label: 'Adv. Intermediate', min: 3.5, max: 4.0, cls: 'pbr-badge-adv-int' },
  { label: 'Advanced',        min: 4.0, max: 4.5, cls: 'pbr-badge-advanced' },
  { label: 'Expert',          min: 4.5, max: 5.0, cls: 'pbr-badge-expert' },
  { label: 'Professional',    min: 5.0, max: 6.0, cls: 'pbr-badge-pro' },
];

function skillBadgeClass(rating) {
  const entry = SKILL_LABELS.find(s => rating >= s.min && rating < s.max)
    || SKILL_LABELS[SKILL_LABELS.length - 1];
  return entry.cls;
}

function SkillBadge({ label }) {
  const entry = SKILL_LABELS.find(s => s.label === label) || SKILL_LABELS[0];
  return (
    <span className={`pbr-badge ${entry.cls}`}>{label}</span>
  );
}

function RatingChange({ change }) {
  if (change > 0) return <span className="pbr-change-pos">+{change.toFixed(2)}</span>;
  if (change < 0) return <span className="pbr-change-neg">{change.toFixed(2)}</span>;
  return <span className="pbr-change-zero">0.00</span>;
}

// ---------------------------------------------------------------------------
// Add Player Modal
// ---------------------------------------------------------------------------
function AddPlayerModal({ onClose, onAdd }) {
  const [form, setForm] = useState({ email: '', name: '', rating: '3.50', partnerName: '', partnerEmail: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) {
      setError('Email and name are required.');
      return;
    }
    const rating = parseFloat(form.rating);
    if (isNaN(rating) || rating < 1 || rating > 6) {
      setError('Rating must be between 1.0 and 6.0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onAdd({
        email: form.email.trim(),
        name: form.name.trim(),
        rating,
        partnerName: form.partnerName.trim() || undefined,
        partnerEmail: form.partnerEmail.trim() || undefined
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pbr-modal-overlay" onClick={onClose}>
      <div className="pbr-modal" onClick={e => e.stopPropagation()}>
        <h3>Add Player</h3>
        {error && <div className="pbr-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="pbr-form-group">
            <label>Email *</label>
            <input name="email" type="email" value={form.email} onChange={handle} placeholder="player@email.com" autoFocus required />
          </div>
          <div className="pbr-form-group">
            <label>Name *</label>
            <input name="name" value={form.name} onChange={handle} placeholder="Jane Smith" required />
          </div>
          <div className="pbr-form-group">
            <label>Starting Rating (1.0–6.0)</label>
            <input name="rating" type="number" step="0.01" min="1" max="6" value={form.rating} onChange={handle} />
          </div>
          <div className="pbr-form-group">
            <label>Partner Name <small>(optional — doubles)</small></label>
            <input name="partnerName" value={form.partnerName} onChange={handle} placeholder="Partner name" />
          </div>
          <div className="pbr-form-group">
            <label>Partner Email <small>(optional)</small></label>
            <input name="partnerEmail" type="email" value={form.partnerEmail} onChange={handle} placeholder="partner@email.com" />
          </div>
          <div className="pbr-modal-actions">
            <button type="button" className="pbr-btn pbr-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="pbr-btn pbr-btn-primary" disabled={saving}>
              {saving ? 'Adding…' : 'Add Player'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Rating Modal
// ---------------------------------------------------------------------------
function EditRatingModal({ player, onClose, onSave }) {
  const [form, setForm] = useState({
    rating: String(player.rating),
    partnerName: player.partnerName || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const rating = parseFloat(form.rating);
    if (isNaN(rating) || rating < 1 || rating > 6) {
      setError('Rating must be between 1.0 and 6.0.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ rating, partnerName: form.partnerName.trim() || null });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pbr-modal-overlay" onClick={onClose}>
      <div className="pbr-modal" onClick={e => e.stopPropagation()}>
        <h3>Adjust Rating — {player.name}</h3>
        {error && <div className="pbr-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="pbr-form-group">
            <label>Rating (1.0–6.0)</label>
            <input name="rating" type="number" step="0.01" min="1" max="6" value={form.rating} onChange={handle} autoFocus />
          </div>
          <div className="pbr-form-group">
            <label>Partner Name <small>(optional)</small></label>
            <input name="partnerName" value={form.partnerName} onChange={handle} placeholder="Partner name" />
          </div>
          <div className="pbr-modal-actions">
            <button type="button" className="pbr-btn pbr-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="pbr-btn pbr-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Player Detail Panel
// ---------------------------------------------------------------------------
function PlayerDetail({ leagueId, email, onClose, onEdit, onDelete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPickleballPlayerRating(leagueId, email)
      .then(res => { if (active) setData(res.player); })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [leagueId, email]);

  if (loading) return <div className="pbr-loading">Loading…</div>;
  if (error) return <div className="pbr-error">{error}</div>;
  if (!data) return null;

  const winPct = data.winPct != null ? Math.round(data.winPct * 100) : '—';

  return (
    <div className="pbr-detail">
      <div className="pbr-detail-header">
        <div>
          <p className="pbr-detail-name">{data.name}</p>
          <p className="pbr-detail-email">{data.email}</p>
          {data.partnerName && (
            <p className="pbr-detail-email">Partner: {data.partnerName}</p>
          )}
          <SkillBadge label={data.skillLabel} />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="pbr-btn pbr-btn-secondary" onClick={onEdit}>Adjust Rating</button>
          <button className="pbr-btn pbr-btn-danger" onClick={onDelete}>Remove</button>
          <button className="pbr-btn pbr-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <div className="pbr-detail-stats">
        <div className="pbr-detail-stat">
          <div className={`pbr-detail-stat-value pbr-rating-big ${skillBadgeClass(data.rating)}`} style={{ background: 'none', padding: 0 }}>
            {data.rating.toFixed(2)}
          </div>
          <div className="pbr-detail-stat-label">Rating</div>
        </div>
        <div className="pbr-detail-stat">
          <div className="pbr-detail-stat-value">{data.gamesPlayed}</div>
          <div className="pbr-detail-stat-label">Games</div>
        </div>
        <div className="pbr-detail-stat">
          <div className="pbr-detail-stat-value">{data.wins}</div>
          <div className="pbr-detail-stat-label">Wins</div>
        </div>
        <div className="pbr-detail-stat">
          <div className="pbr-detail-stat-value">{data.losses}</div>
          <div className="pbr-detail-stat-label">Losses</div>
        </div>
        <div className="pbr-detail-stat">
          <div className="pbr-detail-stat-value">{winPct}{data.winPct != null ? '%' : ''}</div>
          <div className="pbr-detail-stat-label">Win %</div>
        </div>
        <div className="pbr-detail-stat">
          <div className="pbr-detail-stat-value" style={{ color: data.pointDiff >= 0 ? '#16a34a' : '#dc2626' }}>
            {data.pointDiff >= 0 ? '+' : ''}{data.pointDiff}
          </div>
          <div className="pbr-detail-stat-label">Pt. Diff</div>
        </div>
      </div>

      <p className="pbr-history-title">Match History</p>
      {!data.history || data.history.length === 0 ? (
        <p className="pbr-history-empty">No games played yet.</p>
      ) : (
        <div className="pbr-table-wrap">
          <table className="pbr-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Opponent</th>
                <th>Score</th>
                <th>Result</th>
                <th>Opp. Rating</th>
                <th>Change</th>
                <th>New Rating</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map(h => (
                <tr key={h.gameId}>
                  <td>{h.gameDate || '—'}</td>
                  <td>{h.opponentName || '—'}</td>
                  <td className="pbr-stat">{h.myScore ?? '—'} – {h.opponentScore ?? '—'}</td>
                  <td style={{ fontWeight: 600, color: h.won ? '#16a34a' : '#dc2626' }}>
                    {h.won ? 'W' : 'L'}
                  </td>
                  <td className="pbr-stat">{h.opponentRating.toFixed(2)}</td>
                  <td className="pbr-stat"><RatingChange change={h.ratingChange} /></td>
                  <td className="pbr-stat" style={{ fontWeight: 600 }}>{h.ratingAfter.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function PickleballRatingsPage({ leagueId, leagueName, onBack }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null); // player object
  const [recalculating, setRecalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPickleballRatings(leagueId);
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (data) => {
    await addPickleballPlayer(leagueId, data);
    await load();
  };

  const handleEdit = async (data) => {
    await updatePickleballRating(leagueId, showEditModal.email, data);
    await load();
    if (selectedEmail === showEditModal.email) {
      // refresh detail panel
      setSelectedEmail(null);
      setTimeout(() => setSelectedEmail(showEditModal.email), 0);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Remove ${email} from ratings? This cannot be undone.`)) return;
    await deletePickleballRating(leagueId, email);
    if (selectedEmail === email) setSelectedEmail(null);
    await load();
  };

  const handleRecalculate = async () => {
    if (!window.confirm('Recalculate all ratings from game history? This will wipe current ratings and replay every completed game.')) return;
    setRecalculating(true);
    try {
      await recalculatePickleballRatings(leagueId);
      setSelectedEmail(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRecalculating(false);
    }
  };

  const selectedPlayer = leaderboard.find(p => p.email === selectedEmail);

  return (
    <div className="pbr-page">
      {/* Header */}
      <div className="pbr-header">
        <button className="pbr-back-btn" onClick={onBack}>← Back</button>
        <div>
          <h1 className="pbr-title">Player Ratings</h1>
          {leagueName && <p className="pbr-league-name">{leagueName}</p>}
        </div>
        <div className="pbr-actions">
          <button className="pbr-btn pbr-btn-secondary" onClick={handleRecalculate} disabled={recalculating}>
            {recalculating ? <span className="pbr-spinning">↻</span> : '↻'} Recalculate
          </button>
          <button className="pbr-btn pbr-btn-primary" onClick={() => setShowAddModal(true)}>
            + Add Player
          </button>
        </div>
      </div>

      {/* Scale legend */}
      <div className="pbr-scale-legend">
        {SKILL_LABELS.map(s => (
          <span key={s.label} className={`pbr-scale-badge ${s.cls}`}>
            {s.min.toFixed(1)}+ {s.label}
          </span>
        ))}
      </div>

      {error && <div className="pbr-error">{error}</div>}

      {/* Player detail panel (when a row is selected) */}
      {selectedEmail && (
        <PlayerDetail
          leagueId={leagueId}
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onEdit={() => setShowEditModal(selectedPlayer)}
          onDelete={() => handleDelete(selectedEmail)}
        />
      )}

      {/* Leaderboard */}
      {loading ? (
        <div className="pbr-loading">Loading ratings…</div>
      ) : leaderboard.length === 0 ? (
        <div className="pbr-empty">
          <h3>No ratings yet</h3>
          <p>Ratings are updated automatically when game scores are entered.<br />You can also add players manually using the button above.</p>
        </div>
      ) : (
        <div className="pbr-table-wrap">
          <table className="pbr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Skill Level</th>
                <th>Rating</th>
                <th>G</th>
                <th>W</th>
                <th>L</th>
                <th>Win %</th>
                <th>Pt. Diff</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map(p => (
                <tr
                  key={p.email}
                  className="pbr-row-clickable"
                  onClick={() => setSelectedEmail(p.email === selectedEmail ? null : p.email)}
                  style={{ background: p.email === selectedEmail ? 'var(--color-bg-secondary, #f0f4ff)' : undefined }}
                >
                  <td className={`pbr-rank-cell ${p.rank <= 3 ? `pbr-rank-${p.rank}` : ''}`}>
                    {p.rank <= 3 ? ['🥇','🥈','🥉'][p.rank - 1] : p.rank}
                  </td>
                  <td>
                    <div className="pbr-player-name">{p.name}</div>
                    {p.partnerName && <div className="pbr-player-email">w/ {p.partnerName}</div>}
                    <div className="pbr-player-email">{p.email}</div>
                  </td>
                  <td><SkillBadge label={p.skillLabel} /></td>
                  <td><span className="pbr-rating-value">{p.rating.toFixed(2)}</span></td>
                  <td className="pbr-stat">{p.gamesPlayed}</td>
                  <td className="pbr-stat">{p.wins}</td>
                  <td className="pbr-stat">{p.losses}</td>
                  <td className="pbr-stat">
                    {p.winPct != null ? `${Math.round(p.winPct * 100)}%` : '—'}
                  </td>
                  <td className="pbr-stat" style={{ color: p.pointDiff >= 0 ? '#16a34a' : '#dc2626' }}>
                    {p.pointDiff >= 0 ? '+' : ''}{p.pointDiff}
                  </td>
                  <td className="pbr-td-actions" onClick={e => e.stopPropagation()}>
                    <button
                      className="pbr-btn pbr-btn-secondary"
                      style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
                      onClick={() => setShowEditModal(p)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddPlayerModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAdd}
        />
      )}
      {showEditModal && (
        <EditRatingModal
          player={showEditModal}
          onClose={() => setShowEditModal(null)}
          onSave={handleEdit}
        />
      )}
    </div>
  );
}
